/**
 * Single source of truth for PaymentStatus wire-form parsing.
 *
 * Wave 2 of the SoT plan. PaymentStatus is fortunately uniform across
 * the system — Prisma enum, floristy domain type, and BirPay webhook
 * payload all use UPPER_CASE. The risk surface is storefront's
 * PaymentSuccessScreen which compares against literal strings without
 * a type, plus a few `paymentStatus?: string` shapes that lose the
 * union information.
 *
 * Same contract as parseOrderStatus:
 *   - parsePaymentStatus(unknown) -> PaymentStatus | null. Never throws.
 *   - assertPaymentStatus(s) -> asserts canonical. Throws on dirty value.
 *   - isPaymentStatus(s) -> narrow type guard.
 */
const CANONICAL = new Set([
    'PENDING',
    'PROCESSING',
    'SUCCEEDED',
    'CANCELLED',
    'FAILED',
    'REFUNDED',
]);
/** Legacy lowercase form, accepted for the deprecation window. */
const LOWERCASE_LEGACY = Object.freeze(Object.fromEntries(Array.from(CANONICAL).map((v) => [v.toLowerCase(), v])));
/**
 * Parse user input into a canonical PaymentStatus.
 *
 * Accepts canonical UPPER_CASE and lowercase legacy. Returns null on
 * everything else — caller decides whether to log/drop.
 */
export function parsePaymentStatus(raw) {
    if (typeof raw !== 'string')
        return null;
    if (CANONICAL.has(raw))
        return raw;
    return LOWERCASE_LEGACY[raw] ?? null;
}
/** Invariant: must already be canonical UPPER_CASE. Throws on dirty. */
export function assertPaymentStatus(s) {
    if (!CANONICAL.has(s)) {
        throw new Error(`Not a canonical PaymentStatus: "${s}". Expected one of: PENDING, PROCESSING, SUCCEEDED, CANCELLED, FAILED, REFUNDED.`);
    }
}
/** Narrow type guard — true only for canonical UPPER_CASE form. */
export function isPaymentStatus(s) {
    return typeof s === 'string' && CANONICAL.has(s);
}
